/**
 * Pure overlay of server-stored keyword-pin rows onto the code-seeded
 * `DEFAULT_KEYWORD_PINS`. This is the read side of the hybrid persistence model
 * (see docs/plans/2026-06-02-server-backed-keyword-pins.md):
 *
 *   - Built-ins live in code; the server only stores **custom pins** and
 *     **overrides** of built-ins (a disable toggle, or a content edit).
 *   - Override rows carry `builtin: true` and are matched back to a code
 *     default by **label** (built-in labels are locked & unique).
 */
import { toKeywordPin, type KeywordPinView } from './serverTypes';
import type { KeywordPin } from './types';

/**
 * Overlay `serverRows` onto `defaults`, returning the list the UI renders:
 * built-ins (in defaults order) followed by custom pins. Rules:
 * untouched built-in → default verbatim; disable-only → default content + the
 * row's `enabled`; edited (`userEdited`) → row content (keeping the stable code
 * id); custom rows → appended verbatim; orphan override rows (label not in
 * defaults) → dropped.
 */
export function mergeKeywordPins(
	serverRows: ReadonlyArray<KeywordPinView>,
	defaults: ReadonlyArray<KeywordPin>
): KeywordPin[] {
	const overrideByLabel = new Map<string, KeywordPinView>();
	for (const row of serverRows) {
		if (row.builtin) {
			overrideByLabel.set(row.label, row);
		}
	}

	const builtins: KeywordPin[] = defaults.map(def => {
		const row = overrideByLabel.get(def.label);
		if (!row) {
			return { ...def };
		}
		if (row.userEdited) {
			// Edited built-in: the row's content wins, but keep the stable code id
			// so the UI identity (and pin-height/pinned-card keying) is unchanged.
			return { ...toKeywordPin(row), id: def.id, builtin: true };
		}
		// Disable-only: code content is reseeded (default changes propagate), but
		// the user's enabled flag from the row is preserved.
		return { ...def, enabled: row.enabled };
	});

	const custom: KeywordPin[] = serverRows.filter(row => !row.builtin).map(toKeywordPin);

	return [...builtins, ...custom];
}
